import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import { PieChart, Pie, Cell, Legend, Tooltip } from "recharts";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

// Holidays JSON
const holidays = {
  "2025-01-01": "New Year's Day",
  "2025-01-26": "Republic Day",
  "2025-02-26": "Shivratri",
  "2025-03-14": "Holi",
  "2025-03-15": "Bhai Dooj",
  "2025-08-09": "Raksha Bandhan",
  "2025-08-15": "Independence Day",
  "2025-08-16": "Janmashtami",
  "2025-10-02": "Gandhi Jayanti & Dusshera",
  "2025-10-20": "Diwali",
  "2025-10-22": "Goverdhan Pooja",
  "2025-10-23": "Bhai Dooj",
};

const months = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

// Leave stats
const totalLeaves = 22;
const leaveTaken = 15;
const remainingLeaves = totalLeaves - leaveTaken;

// Chart data
const chartData = [
  { name: "Leave Taken", value: leaveTaken, color: "#f06292" }, // pink
  { name: "Remaining Leaves", value: remainingLeaves, color: "#42a5f5" }, // blue
  { name: "National Holidays", value: 6, color: "#fdd835" }, // yellow
];

// Emoji mapping for tooltip
const emojiMap = {
  "Leave Taken": "🩷",
  "Remaining Leaves": "🔵",
  "National Holidays": "🎉",
};

// Custom Tooltip component
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { name, value } = payload[0];
    return (
      <div
        style={{
          backgroundColor: "#000",
          color: "#fff",
          padding: "8px 12px",
          borderRadius: "8px",
          border: "1px solid #444",
          fontSize: "0.9rem",
        }}
      >
        <strong>
          {emojiMap[name]} {name}
        </strong>
        <br />
        <span>{value}</span>
      </div>
    );
  }
  return null;
};

const LeaveCalendar = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [popup, setPopup] = useState({ open: false, message: "", success: false });

  // Format date as YYYY-MM-DD
  const formatDateKey = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
      date.getDate()
    ).padStart(2, "0")}`;

  // Add CSS classes to calendar tiles
  const tileClassName = ({ date }) => {
    const dateKey = formatDateKey(date);
    const dayOfWeek = date.getDay();

    if (holidays[dateKey]) return "holiday";
    if (dayOfWeek === 0) return "sunday";     // Sunday
    if (dayOfWeek === 6) return "saturday";   // Saturday
    return "";
  };

  // Handle click on date
  const handleDayClick = (date) => {
    const dateKey = formatDateKey(date);
    const dayOfWeek = date.getDay();

    let message = `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    let isSuccess = false;

    if (holidays[dateKey]) {
      message += `\n🎉 ${holidays[dateKey]}`;
      isSuccess = true;
    } else if (dayOfWeek === 0) {
      message += `\n🛌 Sunday`;
    } else if (dayOfWeek === 6) {
      message += `\n🕒 Saturday`;
    } else {
      message = `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}\n📅 Working Day`;
    }

    setPopup({ open: true, message, success: isSuccess });
  };

  // Auto popup for today's date
  useEffect(() => {
    handleDayClick(new Date());
  }, []);

  return (
    <Card sx={{ backgroundColor: "#111", color: "white", borderRadius: 3, p: 2 ,width:'44%'}}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Leave Calendar
        </Typography>

        {/* Leave Stats */}
        <Box
          display="flex"
          justifyContent="center"
          gap={4}
          mb={2}
          sx={{ fontWeight: "bold", fontSize: "1rem" }}
        >
          <span>Total Leaves: {totalLeaves}</span> |
          <span> Leave Taken: {leaveTaken}</span> |
          <span> Remaining Leaves: {remainingLeaves}</span>
        </Box>

        {/* Donut Chart */}
        <Box display="flex" justifyContent="center" mb={3}>
          <PieChart width={280} height={250}>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={3}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>

            {/* 🔥 Custom Tooltip */}
            <Tooltip content={<CustomTooltip />} />

            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value) => (
                <span style={{ color: "white", fontSize: "0.9rem" }}>{value}</span>
              )}
            />
          </PieChart>
        </Box>

        {/* Calendar */}
        <Box
          sx={{
            "& .react-calendar": {
              background: "#1e1e1e",
              border: "none",
              borderRadius: "12px",
              color: "black",
              padding: "10px",
            },
            "& .react-calendar__navigation button": {
              color: "white",
              fontSize: "1.2rem",
              fontWeight: "bold",
            },
            "& .react-calendar__month-view__weekdays": {
              textTransform: "uppercase",
              fontSize: "0.9rem",
              color: "white",
              fontWeight: "bold",
              textAlign: "center",
            },
            "& .react-calendar__tile": {
              borderRadius: "8px",
              textAlign: "center",
              padding: "10px 0",
            },
            "& .react-calendar__tile:enabled:hover, & .react-calendar__tile:enabled:focus": {
              background: "transparent !important",
              color: "inherit !important",
            },
            "& .react-calendar__navigation button:enabled:hover, & .react-calendar__navigation button:enabled:focus": {
              background: "transparent !important",
              color: "inherit !important",
            },
            "& .holiday": {
              background: "#db3434 !important",
              color: "white !important",
            },
            "& .sunday": {
              background: "#db3434 !important",
              color: "white !important",
            },
            "& .saturday": {
              background: "#fdd835 !important",
              color: "black !important",
            },
            display:'flex',justifyContent:'center'
          }}
        >
          <Calendar
            value={selectedDate}
            onChange={setSelectedDate}
            tileClassName={tileClassName}
            formatShortWeekday={(locale, date) =>
              ["S", "M", "T", "W", "T", "F", "S"][date.getDay()]
            }
            formatMonthYear={(locale, date) =>
              date.toLocaleDateString("en-US", { month: "long", year: "numeric" })
            }
            formatDay={(locale, date) => date.getDate()}
          />
        </Box>
      </CardContent>

      {/* Snackbar for popup message */}
      <Snackbar
        open={popup.open}
        autoHideDuration={3000}
        onClose={() => setPopup({ ...popup, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={popup.success ? "success" : "info"}
          sx={{ whiteSpace: "pre-line" }}
        >
          {popup.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default LeaveCalendar;
